import { useClientsQuery } from "@/query/clientQuery"
import { useBookingQuery } from "@/query/bookingQuery"
import { useServicesQuery } from "@/query/serviceQuery"
import { useShopDataQuery } from "@/query/shopDataQuery"
import { useUnavailabilityPrestatereQuery } from "@/query/unavailabilityPrestatereQuery"
import { getPrestateres } from "@/utils/functions/getPrestateres"
import { Client, Diary } from "@okeys/shared-react-components"
import { useMemo } from "react"

export const useCalendarProps = () => {
  const { booking, refetchBooking, pendingBooking, errorBooking } = useBookingQuery()
  const { shopData, refetchShopData, pendingShopData, errorShopData } =
    useShopDataQuery()
  const { services, refetchServices, pendingServices, errorServices } =
    useServicesQuery()
  const {
    clients,
    refetch,
    isError: isErrorClients,
    isLoading: isLoadingClients,
    error: errorClients,
  } = useClientsQuery()

  // Récupérer toutes les indisponibilités du shop (sans prestatereId spécifique)
  const {
    unavailabilityPrestatere,
    isLoadingUnavailabilityPrestatere,
    errorUnavailabilityPrestatere,
    refetchUnavailabilityPrestatere,
  } = useUnavailabilityPrestatereQuery()

  const isPending =
    pendingBooking ||
    pendingShopData ||
    pendingServices ||
    isLoadingClients ||
    isLoadingUnavailabilityPrestatere

  const isError =
    errorBooking ||
    errorShopData ||
    errorServices ||
    isErrorClients ||
    errorUnavailabilityPrestatere

  const error = errorBooking || errorShopData || errorServices || errorClients

  const diaries: Diary[] = useMemo(() => {
    if (!shopData) return []
    const prestateres = getPrestateres(shopData)

    return prestateres.map((prestatere) => ({
      bookings:
        (booking || [])
          .filter((b) => b.prestatereId === prestatere.id)
          .map((b) => ({
            ...b,
            clientName:
              clients?.find((c) => c.clientId === (b.clientId || ""))?.name ||
              "",
          })),
      unavailabilities:
        unavailabilityPrestatere?.filter(
          (unavail) => unavail.prestatereId === prestatere.id
        ) || [],
      schedule: prestatere.schedule || shopData.shopSchedule,
      title: `${prestatere.givenName} ${prestatere.familyName}`,
      prestatereId: prestatere.id,
      color: getPrestatereColor(prestatere.id),
    }))
  }, [shopData, booking, unavailabilityPrestatere, clients])

  const formattedClients: Client[] = useMemo(() => {
    if (!clients) return []

    return clients.map((client) => ({
      clientId: client.clientId,
      name: client.name || "",
      email: client.email,
      keys: 0, // TODO: Récupérer le nombre de clés depuis les données client
    }))
  }, [clients])

  const formattedServices = useMemo(() => {
    if (!services) return []
    return services || []
  }, [services])

  return {
    diaries,
    clients: formattedClients,
    services: formattedServices,
    isPending,
    isError,
    error,
    refetch: async () => {
      await Promise.all([
        refetchBooking?.(),
        refetchShopData?.(),
        refetchServices?.(),
        refetch?.(),
        refetchUnavailabilityPrestatere?.(),
      ])
    },
  }
}

// Fonction temporaire pour assigner des couleurs aux prestataires
function getPrestatereColor(prestatereId: string): string {
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FECA57",
    "#FF9FF3",
  ]
  const index = parseInt(prestatereId.replace(/\D/g, "")) || 0
  return colors[index % colors.length]
}
